/**
 * Motor do radar: mantém o plano (varredura de cada radar em cada quadro) e o
 * pré-carregamento — o mesmo modelo do satélite (`loop/preloader.ts`):
 *
 * - nível 1: TODAS as varreduras do loop são baixadas (blobs de 4–40 KB), as
 *   mais próximas do quadro atual na ordem de reprodução primeiro, e os
 *   radares à vista antes dos de fora;
 * - nível 2: só uma JANELA de quadros fica reprojetada e decodificada, e só
 *   dos radares à vista, no tamanho que a tela pede (afastado, 256 px bastam
 *   para um radar que ocupa 200 px da tela; de perto, o tamanho inteiro);
 * - a reprodução espera o próximo quadro do radar ficar pronto
 *   (`registerFrameGate`), como espera o do satélite — nada pisca nem fica
 *   para trás durante a animação.
 */
import type OlMap from 'ol/Map';
import { buffer, getWidth, intersects, type Extent } from 'ol/extent';
import { create } from 'zustand';
import { detectDeviceProfile, MAX_DECODE_WINDOW } from '../goes/budget';
import type { FrameRequest } from '../goes/frameCache';
import { shallowArrayEqual, subscribeSelector } from '../lib/store';
import { decodeWindow } from '../loop/frames';
import { getPlaybackDirection } from '../loop/playback';
import { registerFrameGate, reservePixelBudget } from '../loop/preloader';
import { loopLoadAllowed, useLoopStore } from '../loop/store';
import { lonLatToMap, mapToLonLat } from '../map/projection';
import { useTimeStore } from '../state/time';
import { useViewStore } from '../state/view';
import { definirGeometria, encerrarWorkers, geometriaDe, radarCache, type LarguraRadar } from './cache';
import { calcularGeometria } from './geometria';
import { centroDoRadar, modeloDoRadar } from './georef';
import { montarPlano, urlsDoPlano, type PlanoRadar } from './plano';
import { useRadarStore } from './store';

export const usePlanoRadar = create<{ plano: PlanoRadar | null }>()(() => ({ plano: null }));

/** Parte do orçamento de pixels decodificados do aparelho que o radar pode usar. */
export const FRACAO_ORCAMENTO = 0.25;
/**
 * O radar RESERVA do satélite só o que usa, em degraus de 5% (a reserva não
 * oscila a cada zoom), entre 5% e `FRACAO_ORCAMENTO`. Na vista inicial os nove
 * radares em 256 px somam ~0,6 Mpx por quadro: um loop de 3 h cabe em 4% do
 * orçamento. A reserva fixa de 25% tirava um degrau de nitidez do satélite.
 */
export const RESERVA_MIN = 0.05;
const DEGRAU_RESERVA = 0.05;

/** Fração do orçamento a reservar para `px` pixels decodificados do radar. */
export function fracaoReservada(px: number, orcamentoTotal: number): number {
  if (!(orcamentoTotal > 0)) return FRACAO_ORCAMENTO;
  const f = Math.ceil(px / orcamentoTotal / DEGRAU_RESERVA - 1e-9) * DEGRAU_RESERVA;
  return Math.min(FRACAO_ORCAMENTO, Math.max(RESERVA_MIN, Number(f.toFixed(2))));
}
/** Janela decodificada: de 2 quadros ao loop inteiro, como a do satélite. */
export const JANELA_MIN = 2;
export const JANELA_MAX = MAX_DECODE_WINDOW;
/** Larguras de bitmap usadas quando a tela não precisa do tamanho inteiro. */
export const DEGRAUS = [256, 384, 512, 768] as const;
/** Ampliar até 15% ao desenhar é imperceptível (mesma folga do satélite). */
const FOLGA_AMPLIACAO = 1.15;
/** Radares até 25% da largura da vista além da borda contam como "à vista". */
const MARGEM_VISTA = 0.25;

/**
 * Largura do bitmap para um radar que ocupa `cssPx` pixels CSS na tela; null
 * = tamanho inteiro da grade (`natural`). Em degraus, para o zoom não
 * redecodificar a cada toque.
 */
export function larguraDecodificacao(cssPx: number, dpr: number, natural: number): number | null {
  const precisa = (cssPx * Math.min(Math.max(1, dpr), 2)) / FOLGA_AMPLIACAO;
  const degrau = DEGRAUS.find((d) => d >= precisa);
  return degrau === undefined || degrau >= natural * 0.9 ? null : degrau;
}

/** Pixels de um bitmap de radar na largura `w` (null = grade inteira `natural`). */
export function pixelsDoBitmap(w: number | null, natural: readonly [number, number]): number {
  return w === null ? natural[0] * natural[1] : w * Math.round((natural[1] * w) / natural[0]);
}

/**
 * Larguras de bitmap com o LOOP INTEIRO em memória — a mesma política do
 * satélite (`loopDecodeWidth`, budget.ts). Se os `n` quadros dos radares à
 * vista não cabem no orçamento com as larguras que a tela pede, o radar mais
 * pesado desce um degrau, e assim por diante, até caber ou até todos estarem
 * no menor degrau. Uma janela menor que o loop gira: a cada passo da animação
 * cada radar à vista é decodificado e reprojetado de novo (~20 ms por radar,
 * 9 radares, 5 q/s = um núcleo inteiro). Um degrau abaixo em movimento não se
 * nota; se nem o menor degrau comporta o loop, a janela gira como antes.
 */
export function largurasParaOLoop(
  desejadas: ReadonlyMap<string, { largura: number | null; natural: readonly [number, number] }>,
  n: number,
  orcamentoPx: number,
): Map<string, number | null> {
  const out = new Map<string, number | null>();
  for (const [id, d] of desejadas) out.set(id, d.largura);
  const px = (id: string) => pixelsDoBitmap(out.get(id) ?? null, desejadas.get(id)!.natural);
  const total = () => [...out.keys()].reduce((s, id) => s + px(id), 0);
  const degrauAbaixo = (id: string): number | null => {
    const atual = out.get(id) ?? null;
    const natural = desejadas.get(id)!.natural[0];
    const menores = DEGRAUS.filter((d) => d < natural * 0.9 && (atual === null || d < atual));
    return menores.length ? menores[menores.length - 1] : null;
  };
  while (Math.max(1, n) * total() > orcamentoPx) {
    let alvo: string | null = null;
    for (const id of out.keys()) {
      if (degrauAbaixo(id) === null) continue;
      if (alvo === null || px(id) > px(alvo)) alvo = id;
    }
    if (alvo === null) break;
    out.set(alvo, degrauAbaixo(alvo));
  }
  return out;
}

/** Quadros na janela decodificada para o orçamento e o custo (px) de cada quadro. */
export function tamanhoJanela(orcamentoPx: number, pxPorQuadro: number): number {
  if (pxPorQuadro <= 0) return JANELA_MAX;
  return Math.max(JANELA_MIN, Math.min(JANELA_MAX, Math.floor(orcamentoPx / pxPorQuadro)));
}

/** Radares cuja imagem cruza a vista (com margem). Sem vista conhecida: todos. */
export function radaresAVista(plano: PlanoRadar | null, vista: Extent | null): Set<string> {
  const out = new Set<string>();
  for (const r of plano?.radares ?? []) {
    const geo = geometriaDe(r.id);
    if (!geo) continue;
    if (!vista || intersects(geo.extent, buffer(vista, getWidth(vista) * MARGEM_VISTA))) out.add(r.id);
  }
  return out;
}

// ---------------------------------------------------------------------------

let mapa: OlMap | null = null;

function vistaAtual(): { extent: Extent | null; resolucao: number | null } {
  const size = mapa?.getSize();
  const view = mapa?.getView();
  if (!mapa || !view || !size || !size[0] || !size[1]) return { extent: null, resolucao: null };
  return { extent: view.calculateExtent(size), resolucao: view.getResolution() ?? null };
}

const dpr = () => (typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1);

/**
 * Radares tratados como "à vista" na última sincronização — os que estão na
 * janela de decodificação. O gate da reprodução e a prontidão da exportação
 * usam ESTE conjunto, e não a vista ao vivo: com a vista ao vivo, um radar que
 * entra na tela num arrasto (ou ao girar o celular) seria exigido antes de
 * ser pedido, e a animação inteira travaria esperando por ele.
 */
let aVistaSincronizada = new Set<string>();

/** Radares à vista na última sincronização (gate, camada e exportação). */
export function radaresAVistaAgora(): Set<string> {
  return aVistaSincronizada;
}

/** Versão da geometria de cada radar (entra na URL como fragmento, ver plano.ts). */
const versoes = new Map<string, string>();

/** Calcula (uma vez por extensão/tamanho) a geometria de cada radar do manifesto. */
function atualizarGeometrias(): void {
  const m = useRadarStore.getState().manifesto;
  for (const r of m?.radares ?? []) {
    // A geometria depende do centro (tabela ou bbox do manifesto) e do tamanho do PNG.
    const chave = `${r.id}|${centroDoRadar(r.id, r.extent).join(',')}|${r.px.join('x')}`;
    if (versoes.get(r.id) === chave) continue;
    try {
      const modelo = modeloDoRadar(r.id, r.extent, r.px);
      const geo = calcularGeometria(modelo, r.px, lonLatToMap, mapToLonLat);
      definirGeometria(r.id, chave, geo, modelo);
      versoes.set(r.id, chave);
    } catch (e) {
      console.warn(`[AmaView] Radar ${r.id} sem geometria:`, e);
    }
  }
}

function versaoCurta(id: string): string {
  const v = versoes.get(id);
  if (!v) return '';
  let h = 0;
  for (let i = 0; i < v.length; i++) h = (h * 31 + v.charCodeAt(i)) | 0;
  return `g${(h >>> 0).toString(36)}`;
}

function recalcularPlano(): void {
  const { visivel, manifesto, produto } = useRadarStore.getState();
  if (!visivel || !manifesto) {
    if (usePlanoRadar.getState().plano) usePlanoRadar.setState({ plano: null });
    return;
  }
  atualizarGeometrias();
  const { frames } = useTimeStore.getState();
  const plano = montarPlano(frames, manifesto, produto, versaoCurta);
  const anterior = usePlanoRadar.getState().plano;
  if (anterior && anterior.produto === plano.produto && mesmoPlano(anterior, plano)) return;
  usePlanoRadar.setState({ plano });
}

function mesmoPlano(a: PlanoRadar, b: PlanoRadar): boolean {
  if (!shallowArrayEqual(a.quadros, b.quadros) || a.radares.length !== b.radares.length) return false;
  return a.radares.every((r, i) => r.id === b.radares[i].id && shallowArrayEqual(r.urls, b.radares[i].urls));
}

/**
 * O quadro `i` do radar está pronto para desenhar (radares à vista)?
 * Exportando, só vale o bitmap no tamanho inteiro (nitidez uniforme no vídeo).
 */
export function quadroRadarPronto(i: number): boolean {
  const { plano } = usePlanoRadar.getState();
  if (!plano || !useRadarStore.getState().visivel) return true;
  const exportando = useLoopStore.getState().exportLocked;
  const aVista = radaresAVistaAgora();
  for (const r of plano.radares) {
    if (!aVista.has(r.id)) continue;
    const url = r.urls[i];
    if (!url) continue;
    const st = radarCache.status(url);
    if (st === 'error') continue;
    if (st !== 'ready' || (exportando && !radarCache.readyAtRequestedWidth(url))) return false;
  }
  return true;
}

/**
 * Largura de bitmap em uso por radar, com histerese (a mesma do satélite):
 * subir de degrau só depois que o zoom assenta (600 ms), descer só com a
 * vista parada (2 s) — sem isso, cada toque no zoom que cruza um degrau
 * mandaria redecodificar a janela inteira (o loop inteiro × 9 radares).
 */
const largurasEmUso = new Map<string, number | null>();
let podeSubir = true;
let podeDescer = true;
const posto = (w: number | null) => (w === null ? Infinity : w);

function larguraComHisterese(id: string, desejada: number | null): number | null {
  const atual = largurasEmUso.get(id);
  if (atual === undefined || (posto(desejada) > posto(atual) && podeSubir) || (posto(desejada) < posto(atual) && podeDescer)) {
    largurasEmUso.set(id, desejada);
    return desejada;
  }
  return atual;
}

/** Sincroniza o cache e devolve quantos pixels o radar mantém decodificados. */
function sincronizarCache(orcamentoPx: number): number {
  const { plano } = usePlanoRadar.getState();
  if (!plano || plano.quadros.length === 0) {
    aVistaSincronizada = new Set();
    radarCache.sync([]);
    publicarProgresso(null);
    return 0;
  }
  const { extent, resolucao } = vistaAtual();
  const aVista = radaresAVista(plano, extent);
  aVistaSincronizada = aVista;
  const { index } = useTimeStore.getState();
  const { playing, mode, exportLocked } = useLoopStore.getState();
  const todoLoop = loopLoadAllowed();
  const n = plano.quadros.length;

  // Largura do bitmap de cada radar à vista: a que a tela pede, descendo de
  // degrau até o loop inteiro caber no orçamento (`largurasParaOLoop`), e
  // depois a histerese do zoom. Exportando, o mapa é redesenhado com mais
  // pixels que a tela (até 4×): o bitmap vai no tamanho inteiro, senão o
  // vídeo sairia com o radar ampliado.
  const desejadas = new Map<string, { largura: number | null; natural: readonly [number, number] }>();
  for (const r of plano.radares) {
    const geo = geometriaDe(r.id);
    if (!geo || !aVista.has(r.id)) continue;
    const css = resolucao ? getWidth(geo.extent) / resolucao : null;
    desejadas.set(r.id, {
      largura: css === null ? null : larguraDecodificacao(css, dpr(), geo.largura),
      natural: [geo.largura, geo.altura],
    });
  }
  const ajustadas = exportLocked ? null : largurasParaOLoop(desejadas, n, orcamentoPx);
  const larguras = new Map<string, number | null>();
  let pxPorQuadro = 0;
  for (const [id, d] of desejadas) {
    const w = ajustadas ? larguraComHisterese(id, ajustadas.get(id) ?? null) : null;
    larguras.set(id, w);
    pxPorQuadro += pixelsDoBitmap(w, d.natural);
  }
  const lugares = tamanhoJanela(orcamentoPx, pxPorQuadro);
  const janela = decodeWindow(index, n, lugares, {
    playing,
    mode,
    direction: getPlaybackDirection(),
  });

  const pedidos: FrameRequest<LarguraRadar>[] = [];
  for (const r of plano.radares) {
    const geo = geometriaDe(r.id);
    if (!geo) continue;
    const perto = aVista.has(r.id);
    const pedir = (i: number, prioridade: number, decode: number | null) => {
      const url = r.urls[i];
      const t = r.tempos[i];
      if (!url || t === null) return;
      pedidos.push({
        url,
        productId: r.chave,
        time: t,
        width: geo.largura,
        priority: prioridade,
        decode,
        decodeWidth: decode === null ? null : (larguras.get(r.id) ?? null),
      });
    };
    // Antes do primeiro play (portal parado na medição mais recente) só entra
    // a varredura do quadro na tela e as já baixadas — como o satélite
    // (loop/preloader.ts).
    const noEscopo = (i: number) => todoLoop || i === index || (!!r.urls[i] && radarCache.isDownloaded(r.urls[i]!));
    // Nível 1: todas as varreduras, do quadro atual em diante; os radares
    // fora da vista depois de todos os de dentro.
    for (let k = 0; k < n; k++) {
      const i = (index + k) % n;
      if (noEscopo(i)) pedir(i, perto ? k : n + k, null);
    }
    // Nível 2: a janela, só dos radares à vista.
    if (perto) janela.forEach((i, p) => noEscopo(i) && pedir(i, p, p));
  }
  radarCache.sync(pedidos);
  publicarProgresso(plano);
  return Math.min(n, lugares) * pxPorQuadro;
}

function publicarProgresso(plano: PlanoRadar | null): void {
  const urls = urlsDoPlano(plano);
  let baixadas = 0;
  let falhas = 0;
  for (const u of urls) {
    if (radarCache.status(u) === 'error') falhas++;
    else if (radarCache.isDownloaded(u)) baixadas++;
  }
  const p = useRadarStore.getState().progresso;
  if (p.baixadas !== baixadas || p.total !== urls.length || p.falhas !== falhas) {
    useRadarStore.setState({ progresso: { baixadas, total: urls.length, falhas } });
  }
}

/** Liga o motor com o mapa. Retorna a função de desligar. */
export function iniciarMotorRadar(map: OlMap): () => void {
  mapa = map;
  const orcamentoTotal = detectDeviceProfile().budgetPixels;
  const orcamento = orcamentoTotal * FRACAO_ORCAMENTO;
  // Reserva no orçamento do satélite: só o que o radar usa (`fracaoReservada`).
  let liberarOrcamento: (() => void) | null = null;
  let reservada = 0;
  const reservar = (px: number) => {
    // Exportando, a nitidez do satélite não muda no meio do vídeo.
    if (!liberarOrcamento || useLoopStore.getState().exportLocked) return;
    const f = fracaoReservada(px, orcamentoTotal);
    if (f === reservada) return;
    reservada = f;
    reservePixelBudget(f);
  };
  let agendado = false;
  const sincronizar = () => {
    if (agendado) return;
    agendado = true;
    queueMicrotask(() => {
      agendado = false;
      reservar(sincronizarCache(orcamento));
    });
  };

  // Os pedidos não dependem do estado do cache — só o progresso. A cada
  // arquivo baixado basta recontar, e no máximo 4× por segundo (refazer os
  // ~2.600 pedidos a cada aviso custaria segundos no download inicial).
  let progressoAgendado: ReturnType<typeof setTimeout> | null = null;
  const agendarProgresso = () => {
    if (progressoAgendado) return;
    progressoAgendado = setTimeout(() => {
      progressoAgendado = null;
      publicarProgresso(usePlanoRadar.getState().plano);
    }, 250);
  };

  // Histerese da largura do bitmap (ver `larguraComHisterese`).
  let timerSubir: ReturnType<typeof setTimeout> | null = null;
  let timerDescer: ReturnType<typeof setTimeout> | null = null;
  const aoMudarZoom = () => {
    podeSubir = false;
    podeDescer = false;
    if (timerSubir) clearTimeout(timerSubir);
    if (timerDescer) clearTimeout(timerDescer);
    timerSubir = setTimeout(() => {
      podeSubir = true;
      sincronizar();
    }, 600);
    timerDescer = setTimeout(() => {
      podeDescer = true;
      sincronizar();
    }, 2000);
  };

  // A vista muda no fim de um movimento e quando o mapa muda de tamanho
  // (janela, tela cheia, celular girado) — com o mesmo centro e zoom.
  const aoMover = () => sincronizar();
  map.on('moveend', aoMover);
  map.on('change:size', aoMover);

  // Com o radar ligado, parte do orçamento de pixels decodificados é dele (o
  // que ele usa, de 5% a 25%): a janela do satélite encolhe na mesma proporção.
  const aoMudarPlano = (plano: PlanoRadar | null) => {
    if (plano && !liberarOrcamento) {
      reservada = RESERVA_MIN;
      liberarOrcamento = reservePixelBudget(RESERVA_MIN);
    }
    if (!plano) {
      liberarOrcamento?.();
      liberarOrcamento = null;
      reservada = 0;
      largurasEmUso.clear();
      // Camada desligada: sai tudo do cache e os workers (com as tabelas de
      // reprojeção, ~4 MB por radar) são encerrados.
      radarCache.sync([]);
      encerrarWorkers();
    }
    sincronizar();
  };

  const unsubs = [
    subscribeSelector(useTimeStore, (s) => s.frames, recalcularPlano),
    subscribeSelector(
      useRadarStore,
      (s) => [s.visivel, s.manifesto, s.produto] as const,
      recalcularPlano,
      (a, b) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2],
    ),
    subscribeSelector(usePlanoRadar, (s) => s.plano, aoMudarPlano),
    subscribeSelector(useTimeStore, (s) => s.index, sincronizar),
    subscribeSelector(useLoopStore, (s) => `${s.playing}|${s.mode}|${s.exportLocked}|${s.loopArmed}`, sincronizar),
    subscribeSelector(useViewStore, (s) => s.resolution, aoMudarZoom),
    radarCache.subscribe(agendarProgresso),
    registerFrameGate(quadroRadarPronto),
  ];
  recalcularPlano();
  aoMudarPlano(usePlanoRadar.getState().plano);
  return () => {
    unsubs.forEach((u) => u());
    map.un('moveend', aoMover);
    map.un('change:size', aoMover);
    if (progressoAgendado) clearTimeout(progressoAgendado);
    if (timerSubir) clearTimeout(timerSubir);
    if (timerDescer) clearTimeout(timerDescer);
    liberarOrcamento?.();
    radarCache.sync([]);
    encerrarWorkers();
    usePlanoRadar.setState({ plano: null });
    aVistaSincronizada = new Set();
    largurasEmUso.clear();
    mapa = null;
  };
}
